import { z } from 'zod';
import { filePath, isoDateTime, sha256, slug, storedVisibility, via, visibility } from './common.js';
import { fileKind } from './kinds.js';
import { revisionChanges, revisionManifest } from './manifest.js';
import { capability, orgRole, principalType, projectRole } from './roles.js';

/** Files at or above this size are uploaded in parts. */
export const MULTIPART_THRESHOLD = 64 * 1024 * 1024;
/** Smallest part size the server hands out (S3 needs >= 5 MiB except the last part). */
export const MIN_PART_SIZE = 64 * 1024 * 1024;
export const MAX_PARTS = 10_000;

export const errorBody = z.object({
  error: z.object({ code: z.string(), message: z.string(), details: z.unknown().optional() }),
});
export type ErrorBody = z.infer<typeof errorBody>;

export const sourceInfo = z.looseObject({
  url: z.string().optional(),
  fetchedAt: z.string().optional(),
  fetchedBy: z.string().optional(),
  tool: z.string().optional(),
});
export type SourceInfo = z.infer<typeof sourceInfo>;

export const fileVersion = z.object({
  id: z.string(),
  fileId: z.string(),
  version: z.number().int().positive(),
  sha256,
  size: z.number().int().nonnegative(),
  metadata: z.record(z.string(), z.unknown()),
  source: sourceInfo.nullable(),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  /** Name (else email) of the uploader, for staff and reviewers. */
  createdByName: z.string().nullable().optional(),
  createdVia: via,
  createdAt: isoDateTime,
});
export type FileVersion = z.infer<typeof fileVersion>;

export const fileEntry = z.object({
  id: z.string(),
  projectId: z.string(),
  path: z.string(),
  kind: fileKind,
  visibility: storedVisibility,
  deletedAt: isoDateTime.nullable(),
  current: fileVersion.nullable(),
});
export type FileEntry = z.infer<typeof fileEntry>;

export const beginUploadRequest = z.object({
  path: filePath,
  size: z.number().int().nonnegative(),
  sha256,
  kind: fileKind.default('other'),
  metadata: z.record(z.string(), z.unknown()).optional(),
  /** `restricted` is accepted by the schema only so the server can refuse it with a clear message. */
  visibility: visibility.optional(),
  note: z.string().max(5000).optional(),
  source: sourceInfo.optional(),
  contentType: z.string().max(200).optional(),
});
export type BeginUploadRequest = z.input<typeof beginUploadRequest>;

export const uploadPart = z.object({ partNumber: z.number().int().positive(), url: z.string() });

export const beginUploadResponse = z.discriminatedUnion('status', [
  /** The organization already held the bytes, or the path's current version is identical: nothing to upload. */
  z.object({
    status: z.literal('done'),
    deduplicated: z.boolean(),
    unchanged: z.boolean(),
    file: fileEntry,
    version: fileVersion,
  }),
  z.object({
    status: z.literal('upload'),
    uploadId: z.string(),
    url: z.string(),
    method: z.literal('PUT'),
    headers: z.record(z.string(), z.string()),
    expiresAt: isoDateTime,
  }),
  z.object({
    status: z.literal('multipart'),
    uploadId: z.string(),
    partSize: z.number().int().positive(),
    parts: z.array(uploadPart),
    expiresAt: isoDateTime,
  }),
]);
export type BeginUploadResponse = z.infer<typeof beginUploadResponse>;

export const completeUploadRequest = z.object({
  parts: z.array(z.object({ partNumber: z.number().int().positive(), etag: z.string() })).optional(),
});
export type CompleteUploadRequest = z.input<typeof completeUploadRequest>;

export const completeUploadResponse = z.object({
  status: z.literal('done'),
  file: fileEntry,
  version: fileVersion,
});
export type CompleteUploadResponse = z.infer<typeof completeUploadResponse>;

export const patchFileRequest = z
  .object({
    /** Merged into the current metadata; `null` removes a key. A change records a new version. */
    metadata: z.record(z.string(), z.unknown()).optional(),
    visibility: storedVisibility.optional(),
    kind: fileKind.optional(),
    note: z.string().max(5000).optional(),
  })
  .refine((v) => v.metadata || v.visibility || v.kind || v.note !== undefined, 'nothing to change');
export type PatchFileRequest = z.input<typeof patchFileRequest>;

export const listFilesQuery = z.object({
  prefix: z.string().optional(),
  kind: fileKind.optional(),
  /** Free text matched against path and metadata. */
  q: z.string().max(200).optional(),
  includeDeleted: z.stringbool().optional(),
  limit: z.coerce.number().int().positive().max(5000).optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
});
export type ListFilesQuery = z.input<typeof listFilesQuery>;

/**
 * Where a project's building is (WGS84 degrees, owner 2026-11-01). `georef`: the model origin from the kit's
 * reading/georef.json (±1 m); `map`: a pin a manager placed by hand; `registry`: an address register (later).
 */
export const locationSource = z.enum(['georef', 'map', 'registry']);
export type LocationSource = z.infer<typeof locationSource>;
export const projectLocation = z.object({
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  source: locationSource,
});
export type ProjectLocation = z.infer<typeof projectLocation>;

export const project = z.object({
  id: z.string(),
  orgId: z.string(),
  org: slug,
  orgName: z.string(),
  slug,
  name: z.string(),
  address: z.string().nullable(),
  landNumber: z.string().nullable(),
  municipality: z.string().nullable(),
  status: z.string(),
  description: z.string().nullable(),
  createdAt: isoDateTime,
  role: projectRole.nullable().optional(),
  capabilities: z.array(capability).optional(),
  /** Time of the project's latest audited action (GET /projects/:id, staff and reviewers only). */
  lastActivityAt: isoDateTime.nullable().optional(),
  /** Where the building is, for the map (WGS84). Null until a manager sets it. */
  location: projectLocation.nullable().optional(),
});
export type Project = z.infer<typeof project>;

/**
 * An address in Staðfangaskrá (HMS's address register, open data, CC BY 4.0; owner 2026-11-01): one staðfang with
 * its land number and WGS84 point (the mean when HMS gives several points for one address).
 */
export const registryAddress = z.object({
  /** Heitinúmer: the address's own id in the register. */
  id: z.string(),
  landNumber: z.string().nullable(),
  /** Street and house number as written, e.g. "Bæjarlind 8". */
  address: z.string(),
  postcode: z.string().nullable(),
  lat: z.number(),
  lon: z.number(),
});
export type RegistryAddress = z.infer<typeof registryAddress>;

/** GET /api/addresses: by land number, or by text ("Bæjarlind 8", "Bæjarlind 8, 201"). */
export const addressQuery = z
  .object({
    q: z.string().trim().min(2).max(200).optional(),
    landNumber: z
      .string()
      .trim()
      .regex(/^\d{1,7}$/, 'digits only')
      .optional(),
  })
  .refine((v) => v.q || v.landNumber, 'give q or landNumber');
export type AddressQuery = z.input<typeof addressQuery>;

/** Change a project's name, place or location (managers; owner 2026-11-01). The slug stays. */
export const updateProjectRequest = z
  .object({
    name: z.string().trim().min(1).max(200),
    address: z.string().trim().max(300).nullable(),
    landNumber: z.string().trim().max(40).nullable(),
    municipality: z.string().trim().max(100).nullable(),
    description: z.string().trim().max(5000).nullable(),
    location: projectLocation.nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'nothing to change');
export type UpdateProjectRequest = z.input<typeof updateProjectRequest>;

/** A project on its organization's page: with its newest publication in force and its last activity. */
export const orgProject = project.extend({
  latestPublication: z
    .object({
      id: z.string(),
      title: z.string().nullable(),
      revisionLabel: z.string(),
      publishedAt: isoDateTime,
    })
    .nullable(),
  /** The newest event the caller may know of: any audit event for read:all, else the newest publication. */
  lastActivityAt: isoDateTime.nullable(),
  /**
   * The project's picture: for staff (read:all) the still of the newest revision that has one; for others the still of
   * the newest publication in force.
   */
  stillUrl: z.string().nullable().optional(),
});
export type OrgProject = z.infer<typeof orgProject>;

/**
 * What waits on the project's staff (callers with write:all): the newest frozen state when no publication shows it,
 * and inbox/ files changed since the newest frozen state (partners' uploads).
 */
export const projectAttention = z.object({
  undelivered: z.object({ id: z.string(), label: z.string(), createdAt: isoDateTime }).nullable(),
  inbox: z.number().int().nonnegative(),
});
export type ProjectAttention = z.infer<typeof projectAttention>;

/** A row of the home page's register (GET /api/projects). */
export const projectListItem = orgProject.extend({ attention: projectAttention.optional() });
export type ProjectListItem = z.infer<typeof projectListItem>;

export const createProjectRequest = z.object({
  slug,
  name: z.string().min(1).max(200),
  address: z.string().max(300).optional(),
  landNumber: z.string().max(40).optional(),
  municipality: z.string().max(100).optional(),
  description: z.string().max(5000).optional(),
  status: z.string().max(40).optional(),
});
export type CreateProjectRequest = z.input<typeof createProjectRequest>;

export const createOrgRequest = z.object({
  slug,
  name: z.string().min(1).max(200),
  /** Email of the first owner; the user is created on first sign-in if needed. */
  ownerEmail: z.email().optional(),
});
export type CreateOrgRequest = z.input<typeof createOrgRequest>;

export const accessPrincipal = z.object({
  /** `email` grants to a person by address: stored against the user if one exists, else pending until sign-in. */
  type: principalType,
  /** User id, organization slug or id, or email address. */
  id: z.string().min(1).max(320),
});

export const putAccessRequest = z
  .object({
    grant: z.array(accessPrincipal.extend({ role: projectRole })).default([]),
    revoke: z.array(accessPrincipal).default([]),
    /** Email the people granted by address (a sign-in link). */
    notify: z.boolean().optional(),
  })
  .refine((v) => v.grant.length + v.revoke.length > 0, 'nothing to change');
export type PutAccessRequest = z.input<typeof putAccessRequest>;

export const accessGrant = z.object({
  principalType,
  principalId: z.string(),
  role: projectRole,
  label: z.string(),
  invitedBy: z.string().nullable(),
  createdAt: isoDateTime,
});
export type AccessGrant = z.infer<typeof accessGrant>;

export const createRevisionRequest = z.object({ manifest: revisionManifest });
export type CreateRevisionRequest = z.input<typeof createRevisionRequest>;

export const revision = z.object({
  id: z.string(),
  projectId: z.string(),
  label: z.string(),
  manifest: z.record(z.string(), z.unknown()),
  createdBy: z.string().nullable(),
  /** Name (else email) of the person who froze it; staff views only. */
  createdByName: z.string().nullable().optional(),
  createdAt: isoDateTime,
  /** Changes since the previous revision (staff views only). */
  changes: revisionChanges.optional(),
  /** The model still, when one is attached (see still.ts). */
  stillUrl: z.string().nullable().optional(),
});
export type Revision = z.infer<typeof revision>;

export const auditEvent = z.object({
  id: z.string(),
  orgId: z.string().nullable(),
  projectId: z.string().nullable(),
  actorUserId: z.string().nullable(),
  actorEmail: z.string().nullable().optional(),
  via,
  action: z.string(),
  target: z.string().nullable(),
  data: z.unknown(),
  at: isoDateTime,
});
export type AuditEvent = z.infer<typeof auditEvent>;

export const me = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    isSuperadmin: z.boolean(),
  }),
  orgs: z.array(z.object({ id: z.string(), slug, name: z.string(), role: orgRole })),
  /** How this request was authenticated. */
  auth: z.enum(['session', 'api-key', 'oauth']),
  /** Signed in but given nothing yet: no organization and no project (the "waiting for access" page). */
  pending: z.boolean(),
  /** Superadmins: people waiting for access. */
  pendingUsers: z.number().int().nonnegative().optional(),
  /** Pending invitations to this (verified) address, to accept right after sign-in. */
  invitations: z.array(z.lazy(() => invitationView)).optional(),
  apiKey: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      scope: z.enum(['read', 'write']),
      projects: z.array(z.string()).nullable(),
    })
    .nullable()
    .optional(),
});
export type Me = z.infer<typeof me>;

export const org = z.object({
  id: z.string(),
  slug,
  name: z.string(),
  createdAt: isoDateTime,
  /** Icelandic company id, 10 digits (owners/admins set it; owner 2026-11-01). */
  kennitala: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
});
export type Org = z.infer<typeof org>;

/** Owners and admins of an organization edit its profile; the slug stays. */
export const updateOrgRequest = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  kennitala: z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s-]/g, ''))
    .pipe(z.string().regex(/^\d{10}$/, 'ten digits, such as 010101-0190'))
    .nullable()
    .optional(),
  address: z.string().trim().max(300).nullable().optional(),
});
export type UpdateOrgRequest = z.input<typeof updateOrgRequest>;

/** A member of an organization as its page shows it. */
export const orgMember = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  role: orgRole,
  createdAt: isoDateTime,
  /** Owners and admins only: the newest sign-in on record (null when unknown). */
  lastSignInAt: isoDateTime.nullable().optional(),
  /** Owners and admins only: this organization's projects the member reaches, and how. */
  projects: z.array(z.object({ id: z.string(), slug, name: z.string(), role: projectRole })).optional(),
});
export type OrgMember = z.infer<typeof orgMember>;

export const orgMembersView = z.object({
  org,
  members: z.array(orgMember),
  invitations: z.array(
    z.object({
      id: z.string(),
      email: z.string(),
      role: z.string().nullable(),
      status: z.string(),
      expiresAt: isoDateTime,
    }),
  ),
  /** The organization's verified email domains (names only; superadmins manage them). */
  domains: z.array(z.string()).default([]),
});
export type OrgMembersView = z.infer<typeof orgMembersView>;

export const updateMemberRequest = z.object({ role: orgRole });
export type UpdateMemberRequest = z.input<typeof updateMemberRequest>;

/** API key scope, stored in the key's metadata. */
export const apiKeyScope = z.object({
  scope: z.enum(['read', 'write']).default('write'),
  /** Project ids the key may reach; null for every project the user can reach. */
  projects: z.array(z.string()).nullable().default(null),
});
export type ApiKeyScope = z.infer<typeof apiKeyScope>;

const domainName = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^(?=.{3,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/,
    'a domain such as mynstra.is',
  );

/** A verified email domain of an organization (DESIGN §2). */
export const orgDomain = z.object({
  id: z.string(),
  orgId: z.string(),
  domain: z.string(),
  /** When set, a Microsoft sign-in must come from this Entra tenant (`tid`). */
  entraTenantId: z.string().nullable(),
  defaultOrgRole: orgRole,
  autoJoin: z.boolean(),
  createdAt: isoDateTime,
});
export type OrgDomain = z.infer<typeof orgDomain>;

export const createOrgDomainRequest = z.object({
  domain: domainName,
  entraTenantId: z.guid().nullable().optional(),
  defaultOrgRole: orgRole.default('member'),
  autoJoin: z.boolean().default(true),
});
export type CreateOrgDomainRequest = z.input<typeof createOrgDomainRequest>;

/** Invite an email address to an organization, optionally with a project role granted on acceptance. */
export const createInvitationRequest = z
  .object({
    email: z.email(),
    role: orgRole.default('member'),
    project: z.string().optional(),
    projectRole: projectRole.optional(),
    /** Several of the organization's projects at once (instead of project/projectRole). */
    projects: z
      .array(z.object({ project: z.string(), role: projectRole }))
      .max(50)
      .optional(),
  })
  .refine(
    (v) => Boolean(v.project) === Boolean(v.projectRole),
    'give both project and projectRole, or neither',
  )
  .refine((v) => !(v.project && v.projects?.length), 'give project or projects, not both');
export type CreateInvitationRequest = z.input<typeof createInvitationRequest>;

export const invitationView = z.object({
  id: z.string(),
  orgId: z.string(),
  orgSlug: z.string().optional(),
  orgName: z.string().optional(),
  email: z.string(),
  role: z.string().nullable(),
  /** pending, accepted, canceled, rejected — or expired (pending past its date). */
  status: z.string(),
  expiresAt: isoDateTime,
  createdAt: isoDateTime.optional(),
  inviterName: z.string().nullable().optional(),
  grants: z
    .array(z.object({ projectId: z.string(), projectName: z.string().optional(), role: projectRole }))
    .default([]),
});
export type InvitationView = z.infer<typeof invitationView>;

export const pendingUser = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  emailVerified: z.boolean(),
  createdAt: isoDateTime,
});
export type PendingUser = z.infer<typeof pendingUser>;

/** Superadmins place a waiting user: into an organization, onto a project, or both. */
export const assignUserRequest = z
  .object({
    org: z.string().optional(),
    orgRole: orgRole.default('member'),
    project: z.string().optional(),
    projectRole: projectRole.optional(),
  })
  .refine((v) => v.org || v.project, 'give an organization or a project')
  .refine((v) => !v.project || v.projectRole, 'give projectRole with project');
export type AssignUserRequest = z.input<typeof assignUserRequest>;

/** One line of text: trimmed, no line breaks (it goes into an email subject and headers). */
const line = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .refine((s) => !/[\r\n]/.test(s), 'one line only');

/**
 * A quote request from the public landing page (POST /api/quote, no sign-in). `website` is a honeypot: people never
 * see it, so a value means a bot; the server then answers as if it had worked and sends nothing.
 */
export const quoteRequest = z.object({
  name: line(200).pipe(z.string().min(1, 'required')),
  email: z.string().trim().max(320).pipe(z.email()),
  company: line(200).optional(),
  address: line(300).pipe(z.string().min(1, 'required')),
  message: z.string().trim().max(4000).optional(),
  website: z.string().max(2000).optional(),
  lang: z.enum(['is', 'en']).optional(),
});
export type QuoteRequest = z.input<typeof quoteRequest>;
